import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { InterviewContextZone } from "@/components/build/InterviewContextZone";
import { StarsReadOnly } from "@/components/build/RatingStars";
import { ChevronLeft, ChevronRight, X, History, Ban, ShieldOff } from "lucide-react";

// Past Interviews (2026-02, PART D; rebuilt Part A, 2026-02): read-only browse of COMPLETED
// interview decisions — founder+pm only (build.applications.manage, same as Applications/
// Interviews). The detail panel now reuses the SAME two-zone layout as the live Interview page:
// InterviewContextZone (Context) + a read-only rendering of the Interviewer Capture fields.
// Filtering Decision=Reject here IS the "Rejected" list/view the founder's spec calls for.

const DECISION_STYLES = {
  Onboard: "bg-emerald-500/10 text-emerald-600",
  Hold: "bg-hairline text-ink-soft",
  Reject: "bg-red-500/10 text-red-600",
};

function DecisionPill({ decision }) {
  if (!decision) return <span className="text-faint text-xs">—</span>;
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider whitespace-nowrap ${DECISION_STYLES[decision] || "bg-hairline text-ink-soft"}`}>{decision}</span>;
}

export default function PastInterviews() {
  const { hasScope } = useAuth();
  const canManage = hasScope("build.applications.manage");
  const canClearFlag = hasScope("freelancer_profile.write");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    if (!canManage) { setLoading(false); return; }
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (decisionFilter) params.decision = decisionFilter;
    api.get("/v1/build/interviews/history", { params }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    }).finally(() => setLoading(false));
  }, [page, pageSize, search, decisionFilter, canManage]);

  useEffect(load, [load]);
  useEffect(() => { setPage(1); }, [search, decisionFilter]);

  const openDetail = (id) => {
    setDetailId(id);
    setDetailLoading(true);
    api.get(`/v1/build/interviews/history/${id}`).then((r) => setDetail(r.data)).finally(() => setDetailLoading(false));
  };

  const clearFlag = async () => {
    if (!detail) return;
    setClearing(true);
    try {
      await api.post(`/v1/build/freelancers/${detail.id}/clear-do-not-re-accept`);
      toast.success("Do Not Re-Accept cleared — this email can re-apply now.");
      setDetail({ ...detail, do_not_re_accept: false });
      setItems((prev) => prev.map((i) => (i.id === detail.id ? { ...i, do_not_re_accept: false } : i)));
    } catch {
      toast.error("Couldn't clear the flag");
    } finally { setClearing(false); }
  };

  if (!canManage) {
    return <div className="p-8 text-sm text-faint" data-testid="past-interviews-restricted">Access Restricted — founder/pm only.</div>;
  }

  return (
    <div className="p-5 sm:p-10 space-y-6" data-testid="past-interviews-page">
      <div>
        <div className="font-editorial text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink inline-flex items-center gap-2.5">
          <History className="h-7 w-7 text-brand" /> Past Interviews
        </div>
        <p className="text-sm text-ink-soft mt-1.5">{total} completed interview{total === 1 ? "" : "s"} on record. Filter Decision = Reject for the Rejected list.</p>
      </div>

      <div className="rounded-lg border border-hairline bg-paper p-4 sm:p-5 flex items-end gap-4 flex-wrap" data-testid="past-interviews-filter-bar">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-faint">Search</label>
          <input
            data-testid="past-interviews-search-input" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
            onBlur={() => setSearch(searchInput)}
            placeholder="Search by name…"
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-52 focus:border-brand outline-none transition-colors"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-faint">Decision</label>
          <select data-testid="past-interviews-decision-filter" value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-40 focus:border-brand outline-none transition-colors">
            <option value="">All</option>
            <option value="Onboard">Onboard</option>
            <option value="Hold">Hold</option>
            <option value="Reject">Reject</option>
          </select>
        </div>
      </div>

      <div className="rounded-lg border border-hairline bg-paper overflow-hidden" data-testid="past-interviews-table">
        {loading ? (
          <div className="p-8 text-center text-sm text-faint">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState title="No completed interviews yet" hint="Once a Selected candidate is interviewed (Onboard/Hold/Reject), it shows up here." testId="past-interviews-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wider text-faint">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Interview Date</th>
                <th className="px-4 py-3 font-medium">Interviewer</th>
                <th className="px-4 py-3 font-medium">Overall Rating</th>
                <th className="px-4 py-3 font-medium">Expected Cost</th>
                <th className="px-4 py-3 font-medium">Decision</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {items.map((r) => (
                <tr key={r.id} data-testid={`past-interview-row-${r.id}`} onClick={() => openDetail(r.id)}
                  className="hover:bg-surface2 transition-colors cursor-pointer">
                  <td className="px-4 py-3 text-ink font-medium">
                    {r.name}<span className="ml-2 text-ink-soft font-normal">{r.primary_role}</span>
                    {r.do_not_re_accept && (
                      <span title="Do Not Re-Accept" className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-600">
                        <ShieldOff className="h-2.5 w-2.5" /> No Re-Accept
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{r.interview_date || "—"}</td>
                  <td className="px-4 py-3 text-ink-soft">{r.interviewer || "—"}</td>
                  <td className="px-4 py-3"><StarsReadOnly value={r.njm_rating} /></td>
                  <td className="px-4 py-3 text-ink-soft">{r.expected_cost_per_project || (r.day_rate_quoted != null ? `₹${r.day_rate_quoted}` : "—")}</td>
                  <td className="px-4 py-3"><DecisionPill decision={r.interview_decision} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="past-interviews-pagination">
          <div className="text-xs text-faint">Page {page} of {totalPages}</div>
          <div className="flex items-center gap-2">
            <button data-testid="past-interviews-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="inline-flex items-center gap-1 rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft hover:border-brand disabled:opacity-40">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <button data-testid="past-interviews-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft hover:border-brand disabled:opacity-40">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {detailId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="past-interview-detail-modal" onClick={() => setDetailId(null)}>
          <div className="max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-lg border border-hairline bg-paper p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <div className="text-center text-sm text-faint py-8">Loading…</div>
            ) : (
              <>
                <div className="flex items-start justify-between">
                  <div className="text-xs text-faint">Completed interview</div>
                  <button onClick={() => setDetailId(null)} data-testid="past-interview-detail-close" className="text-faint hover:text-ink"><X className="h-4 w-4" /></button>
                </div>

                <InterviewContextZone detail={detail} />

                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Bio</div>
                  <p className="text-sm text-ink whitespace-pre-wrap">{detail.bio || <span className="text-faint italic">Not provided.</span>}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 rounded-md border border-hairline p-4" data-testid="past-interview-capture-zone">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Interview Date</div>
                    <div className="text-sm text-ink mt-0.5">{detail.interview_date || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Interviewed By</div>
                    <div className="text-sm text-ink mt-0.5">{detail.interviewer || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Attitude Rating</div>
                    <div className="mt-0.5"><StarsReadOnly value={detail.attitude_rating} testId="past-interview-detail-attitude-rating" /></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Overall Rating</div>
                    <div className="mt-0.5"><StarsReadOnly value={detail.njm_rating} testId="past-interview-detail-overall-rating" /></div>
                  </div>
                  {detail.attitude_notes && (
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-wider text-faint">Attitude Notes</div>
                      <div className="text-sm text-ink mt-0.5 whitespace-pre-wrap">{detail.attitude_notes}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Expected Cost per Project</div>
                    <div className="text-sm text-ink mt-0.5">{detail.expected_cost_per_project || (detail.day_rate_quoted != null ? `₹${detail.day_rate_quoted}/day` : "—")}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Availability</div>
                    <div className="text-sm text-ink mt-0.5">{detail.availability_notes || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-faint">Decision</div>
                    <div className="mt-0.5"><DecisionPill decision={detail.interview_decision} /></div>
                  </div>
                  {detail.interview_decision === "Reject" && detail.decline_reason && (
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-wider text-faint">Decline Reason</div>
                      <div className="text-sm text-ink mt-0.5">{detail.decline_reason}</div>
                    </div>
                  )}
                  {detail.interview_decision === "Onboard" && detail.onboarded_on && (
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-wider text-faint">Onboarded On</div>
                      <div className="text-sm text-ink mt-0.5">{detail.onboarded_on}</div>
                    </div>
                  )}
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-faint mb-2">Additional Notes</div>
                    <p className="text-sm text-ink whitespace-pre-wrap" data-testid="past-interview-detail-notes">{detail.interview_notes || <span className="text-faint italic">None recorded.</span>}</p>
                  </div>
                </div>

                {detail.do_not_re_accept && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4 flex items-center justify-between gap-3" data-testid="past-interview-do-not-re-accept-banner">
                    <div className="flex items-start gap-2 text-xs text-red-800">
                      <Ban className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>Do Not Re-Accept is flagged — this email is permanently blocked from re-applying.</span>
                    </div>
                    {canClearFlag && (
                      <button data-testid="past-interview-clear-do-not-re-accept-btn" onClick={clearFlag} disabled={clearing}
                        className="shrink-0 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 whitespace-nowrap">
                        {clearing ? "Clearing…" : "Clear flag"}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
